import { Link, useNavigate, useParams } from "react-router";

import { FormPageHeading } from "@/shared/components";
import { OrgRoleOnly } from "@/shared/components/OrgRoleOnly";
import { ONLY_ADMINS_CHANGE_RULES } from "@/shared/domain/ingestionRules";
import {
  useIngestionRule,
  useUpdateIngestionRule,
} from "@/shared/domain/ingestionRules/hooks";
import { Callout, Spinner } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { type RuleSavedState } from "./IngestionRuleDetail";
import { IngestionRuleForm } from "./IngestionRuleForm";
import styles from "./IngestionRulePages.module.css";
import { IngestionRulesChrome } from "./IngestionRulesChrome";

/** Edit one ingestion rule; saving rewrites its managed trigger. Org admins only. */
export function EditIngestionRule() {
  const { ruleId } = useParams<{ ruleId: string }>();
  const navigate = useNavigate();
  const ruleQuery = useIngestionRule(ruleId);
  const { mutateAsync: updateRule } = useUpdateIngestionRule();
  const rule = ruleQuery.data;

  return (
    <IngestionRulesChrome
      title="Edit"
      crumbs={
        rule !== undefined && (
          <Link to={Goto.createIngestionRuleRoute(rule.rule_id)}>
            {rule.name}
          </Link>
        )
      }
    >
      <div className={styles.page}>
        <FormPageHeading
          title="Edit ingestion rule"
          subtitle="Changes apply to files uploaded after you save."
        />
        {ruleQuery.isLoading && <Spinner />}
        {ruleQuery.error !== null && (
          <Callout variant="error">
            {userFacingErrorMessage(ruleQuery.error)}
          </Callout>
        )}
        {rule !== undefined && (
          <OrgRoleOnly
            role="admin"
            fallback={<Callout>{ONLY_ADMINS_CHANGE_RULES}</Callout>}
          >
            <IngestionRuleForm
              mode="edit"
              initial={rule}
              onSubmit={async (request) => {
                await updateRule({ ruleId: rule.rule_id, updates: request });
                void navigate(Goto.createIngestionRuleRoute(rule.rule_id), {
                  state: { ruleSaved: true } satisfies RuleSavedState,
                });
              }}
              onCancel={() =>
                void navigate(Goto.createIngestionRuleRoute(rule.rule_id))
              }
            />
          </OrgRoleOnly>
        )}
      </div>
    </IngestionRulesChrome>
  );
}
